import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CONFIGURATION_SERVICE, IConfigurationService } from '../configuration';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { inject, injectable } from 'inversify';
import { IPlaceOrder, PLACE_ORDER_SERVICE } from '../place-order';
import { ISpeedBumpService } from './speed-bump.interface';
import { ITradingSettingsProviderService, TRADING_SETTINGS_PROVIDER_SERVICE } from '../../services/trading-settings-provider';

@injectable()
export class SpeedBumpService implements ISpeedBumpService {
    @inject(CONFIGURATION_SERVICE) private readonly configurationSrv: IConfigurationService;
    @inject(PLACE_ORDER_SERVICE) private readonly placeOrderSrv: IPlaceOrder;
    @inject(TRADING_SETTINGS_PROVIDER_SERVICE) private readonly settingsProvider: ITradingSettingsProviderService;

    private readonly destroyed$ = new Subject<boolean>();
    private speedBumpState$: BehaviorSubject<boolean>;
    private tradingClickThrottleTime = 5_000;
    private shouldApplySpeedBump = true;

    speedBumpStateObservable(): Observable<boolean> {
        if (!Boolean(this.speedBumpState$)) {
            this.init();
        }

        return this.speedBumpState$.asObservable();
    }

    private init(): void {
        this.speedBumpState$ = new BehaviorSubject<boolean>(false);

        this.configurationSrv.asObservable().pipe(
            map(config => config.tradingClickThrottle),
            takeUntil(this.destroyed$),
        ).subscribe((tradingClickThrottleTime) => {
            this.tradingClickThrottleTime = tradingClickThrottleTime;
        });

        this.settingsProvider.stateObservable('hasInHouseAttached').pipe(
            takeUntil(this.destroyed$),
        ).subscribe((hasInHouseAttached) => {
            this.shouldApplySpeedBump = !hasInHouseAttached;
        });

        this.placeOrderSrv.orderRequestObservable().pipe(
            finalize(() => this.stop()),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            if (this.shouldApplySpeedBump) {
                this.speedBumpState$.next(true);

                setTimeout(() => {
                    this.speedBumpState$.next(false);
                }, this.tradingClickThrottleTime);
            }
        });
    }

    protected stop(): void {
        this.destroyed$.next(true);
    }
}
